import { Head, Link } from '@inertiajs/react';
import { ArrowRight, Heart, ShoppingBag, Trash2 } from 'lucide-react';

import { Container } from '@/theme3/components/common/Container';
import { ProductCard } from '@/theme3/components/product/ProductCard';
import { useWishlist } from '@/theme3/hooks/useWishlist';

export default function Wishlist() {
    const {
        wishlistProducts,
        wishlistCount,
        clearWishlist,
        removeFromWishlist,
        moveToCart,
    } = useWishlist();

    const totalValue = wishlistProducts.reduce(
        (sum, product) => sum + Number(product.price || 0),
        0,
    );

    return (
        <>
            <Head title="Wishlist - Alpona" />

            <main className="py-6 sm:py-8 lg:py-10">
                <Container className="space-y-8">
                    <section className="overflow-hidden rounded-[2.5rem] border border-rose-100 bg-[linear-gradient(135deg,#f51b75_0%,#d80b67_100%)] p-7 text-white shadow-[0_24px_80px_rgba(245,27,117,0.18)] sm:p-10">
                        <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
                            <div className="max-w-2xl">
                                <p className="text-[10px] font-black uppercase tracking-[0.32em] text-white/60">
                                    Saved Items
                                </p>
                                <h1 className="mt-3 text-4xl font-black uppercase tracking-tight sm:text-6xl">
                                    Wishlist
                                </h1>
                                <p className="mt-4 max-w-xl text-sm leading-7 text-white/75 sm:text-base">
                                    Keep your favorite beauty products in one place and
                                    return whenever you are ready to shop.
                                </p>
                            </div>

                            <div className="grid gap-3 sm:min-w-[260px]">
                                <Stat label="Items" value={wishlistCount} />
                                <Stat
                                    label="Estimated value"
                                    value={`৳${totalValue.toLocaleString('en-BD')}`}
                                />
                            </div>
                        </div>
                    </section>

                    {wishlistCount > 0 ? (
                        <section className="space-y-5">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <p className="text-sm font-semibold text-slate-600">
                                    {wishlistCount} saved {wishlistCount === 1 ? 'item' : 'items'}
                                </p>

                                <button
                                    type="button"
                                    onClick={clearWishlist}
                                    className="inline-flex items-center gap-2 rounded-full border border-rose-200 bg-white px-4 py-2 text-sm font-black uppercase tracking-[0.16em] text-[#d80b67] transition hover:-translate-y-0.5 hover:bg-[#fff7fa]"
                                >
                                    <Trash2 className="size-4" />
                                    Clear wishlist
                                </button>
                            </div>

                            <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
                                {wishlistProducts.map((product) => (
                                    <ProductCard
                                        key={product.slug}
                                        product={product}
                                        layout="portrait"
                                        subtitle={product.brand || product.category || 'Beauty essential'}
                                        badgeLabel={product.isSoldOut ? 'Sold out' : 'Saved'}
                                        statusLabel={product.isSoldOut ? 'Sold out' : 'Saved'}
                                        emptyStateLabel="Preview coming soon"
                                        emptyStateEyebrow="Beauty product"
                                        primaryActionLabel="Cart"
                                        secondaryActionLabel="Remove"
                                        footerActions={
                                            <div className="grid grid-cols-2 gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => moveToCart(product)}
                                                    className="inline-flex items-center justify-center gap-2 rounded-full bg-[#f51b75] px-4 py-3 text-xs font-black uppercase tracking-[0.16em] text-white transition hover:bg-[#d80b67]"
                                                >
                                                    <ShoppingBag className="size-4" />
                                                    Cart
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => removeFromWishlist(product.slug)}
                                                    className="inline-flex items-center justify-center gap-2 rounded-full border border-rose-200 bg-white px-4 py-3 text-xs font-black uppercase tracking-[0.16em] text-[#d80b67] transition hover:bg-[#fff7fa]"
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        }
                                    />
                                ))}
                            </div>
                        </section>
                    ) : (
                        <section className="rounded-[2rem] border border-dashed border-rose-200 bg-white/80 px-6 py-16 text-center shadow-[0_18px_44px_rgba(236,72,153,0.08)]">
                            <div className="mx-auto flex size-16 items-center justify-center rounded-full bg-[#fff1f5] text-[#d80b67]">
                                <Heart className="size-7" />
                            </div>
                            <h2 className="mt-6 text-3xl font-black uppercase tracking-tight text-slate-950">
                                Your wishlist is empty
                            </h2>
                            <p className="mx-auto mt-3 max-w-lg text-sm leading-7 text-slate-600">
                                Save items you love, compare later, and come back when you
                                are ready to checkout.
                            </p>

                            <Link
                                href="/shop"
                                className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#f51b75] px-6 py-3 text-sm font-black uppercase tracking-[0.16em] text-white transition hover:-translate-y-0.5 hover:bg-[#d80b67]"
                            >
                                Start shopping
                                <ArrowRight className="size-4" />
                            </Link>
                        </section>
                    )}
                </Container>
            </main>
        </>
    );
}

function Stat({ label, value }) {
    return (
        <div className="rounded-[1.5rem] border border-white/20 bg-white/12 px-5 py-4 backdrop-blur-xl">
            <p className="text-[10px] font-black uppercase tracking-[0.28em] text-white/60">
                {label}
            </p>
            <p className="mt-2 text-xl font-black tracking-tight text-white">{value}</p>
        </div>
    );
}
